import { NSARouterMakerResult } from "namirasoft-account-react";
import { ClassifierClassInputRow, ClassifierFullRow, ClassifierInputRow, ClassifierRow, ClassifierInferencerType } from "namirasoft-inference";
import { NSBox, NSBoxBoolean, NSBoxString, NSBoxTextArea, NSColumn, NSRepeater, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import ClassifierClassesView from "../components/ClassifierClassesView";
import ClassifierClassLine from "../components/ClassifierClassLine";
import InferencerCommonFields from "../components/InferencerCommonFields";
import InferencerTypeID from "../components/InferencerTypeID";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface ClassifierEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

export default class ClassifierEditPage extends Component<ClassifierEditPageProps>
{
    NSRepeater_Classes = createRef<NSRepeater<ClassifierClassLine, ClassifierClassInputRow>>();
    NSBoxString_Name = createRef<NSBoxString>();
    InferencerTypeID_Inferencer = createRef<InferencerTypeID<ClassifierInferencerType>>();
    NSBoxTextArea_AdditionalPrompt = createRef<NSBoxTextArea>();
    NSBoxBoolean_Multiple = createRef<NSBoxBoolean>();
    InferencerCommonFields_Ids = createRef<InferencerCommonFields>();
    NSBoxString_Description = createRef<NSBoxString>();

    override render()
    {
        return (
            <InferenceLayout<ClassifierRow, ClassifierInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.classifier.edit : InferenceInfo.classifier.create }}
                pages={{
                    entity: InferenceEntities.classifier(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): ClassifierInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let inferencer = this.InferencerTypeID_Inferencer.current?.getValue() ?? { inferencer_type: ClassifierInferencerType.MODEL, inferencer_id: "" };
                                        let inferencer_type = inferencer.inferencer_type;
                                        let inferencer_id = inferencer.inferencer_id;
                                        let additional_prompt = this.NSBoxTextArea_AdditionalPrompt.current?.getValue() ?? null;
                                        let multiple = this.NSBoxBoolean_Multiple.current?.getValue() ?? false;
                                        let { memory_id, wiper_id, log_group_id } = this.InferencerCommonFields_Ids.current?.getValue() ?? InferencerCommonFields.EMPTY;
                                        let description = this.NSBoxString_Description.current?.getValue() ?? null;
                                        let classes = this.NSRepeater_Classes.current?.getValue() ?? [];

                                        return {
                                            workspace_id: null,
                                            name,
                                            inferencer_type,
                                            inferencer_id,
                                            additional_prompt,
                                            multiple,
                                            memory_id,
                                            wiper_id,
                                            log_group_id,
                                            description,
                                            classes,
                                            classifier_category: [],
                                            classifier_field: [],
                                            classifier_tag: [],
                                        };
                                    },
                                    setRow: (row: ClassifierRow) =>
                                    {
                                        let full = row as ClassifierFullRow;

                                        this.NSBoxString_Name.current?.setValue(full.name);
                                        this.InferencerTypeID_Inferencer.current?.setValue({ inferencer_type: full.inferencer_type, inferencer_id: full.inferencer_id });
                                        this.NSBoxTextArea_AdditionalPrompt.current?.setValue(full.additional_prompt);
                                        this.NSBoxBoolean_Multiple.current?.setValueBool(full.multiple);
                                        this.InferencerCommonFields_Ids.current?.setValue({
                                            memory_id: full.memory_id,
                                            wiper_id: full.wiper_id,
                                            log_group_id: full.log_group_id,
                                        });
                                        this.NSBoxString_Description.current?.setValue(full.description);
                                        this.NSRepeater_Classes.current?.setValue((full.classes ?? []).map(c => ({
                                            name: c.name,
                                            description: c.description,
                                        })));
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Classes",
                                            clicked: true,
                                            getContent: () => (
                                                <NSRepeater<ClassifierClassLine, ClassifierClassInputRow>
                                                    ref={this.NSRepeater_Classes}
                                                    title="Classes"
                                                    required
                                                    min={1}
                                                    createItem={(gIndex: number, lIndex: number, ref: React.RefObject<ClassifierClassLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                                                    {
                                                        return <ClassifierClassLine
                                                            ref={ref}
                                                            {...this.props}
                                                            key={`ClassLine_${gIndex}`}
                                                            index={lIndex}
                                                            deletable={true}
                                                            onChanged={() => { onChange(); }}
                                                            onDelete={() => { onDelete?.(lIndex); }}
                                                        />;
                                                    }}
                                                    isEmpty={function (_, value: ClassifierClassInputRow | null): boolean
                                                    {
                                                        return !ClassifierClassLine.IS_DIRTY(value);
                                                    }}
                                                    getValue={function (item: ClassifierClassLine, checkError: boolean): ClassifierClassInputRow | null
                                                    {
                                                        return item.getValue(checkError);
                                                    }}
                                                    setValue={function (item: ClassifierClassLine, _: number, value: ClassifierClassInputRow | null): void
                                                    {
                                                        if (value)
                                                            item.setValue(value);
                                                    }}
                                                    style={{ width: NSBox.width.triple }}
                                                />
                                            )
                                        },
                                        {
                                            title: "Common",
                                            clicked: true,
                                            getContent: () => (
                                                <InferencerCommonFields
                                                    ref={this.InferencerCommonFields_Ids}
                                                    {...this.props}
                                                    hide={{ validator: true, budget: true }}
                                                    info_entity="classifier"
                                                />
                                            ),
                                        },
                                    ]
                                }
                            },
                            getChildrenAfter: (id, row) =>
                            {
                                if (!id || !row)
                                    return null;
                                let full = row as ClassifierFullRow;
                                return (
                                    <ClassifierClassesView
                                        {...this.props}
                                        classifier_id={id}
                                        rows={full.classes ?? []}
                                    />
                                );
                            },
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSRow>
                        <NSBoxString
                            ref={this.NSBoxString_Name}
                            required
                            title="Name"
                            info={{ link: "https://namirasoft.com/inference/console-guide/classifier/#field-name" }}
                        />
                        <NSBoxBoolean
                            ref={this.NSBoxBoolean_Multiple}
                            title="Multiple"
                            required={false}
                            info={{ link: "https://namirasoft.com/inference/console-guide/classifier/#field-multiple" }}
                        />
                    </NSRow>
                    <NSRow>
                        <InferencerTypeID<ClassifierInferencerType>
                            ref={this.InferencerTypeID_Inferencer}
                            {...this.props}
                            typeTitle="Inferencer Type"
                            idTitle="Inferencer ID"
                            required
                            getEnumObject={() => ClassifierInferencerType}
                            defaultType={ClassifierInferencerType.MODEL}
                            info={{ link: "https://namirasoft.com/inference/console-guide/classifier/#field-inferencer" }}
                        />
                    </NSRow>
                    <NSBoxTextArea
                        ref={this.NSBoxTextArea_AdditionalPrompt}
                        required={false}
                        title="Additional Prompt"
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/classifier/#field-additional-prompt" }}
                    />
                    <NSBoxString
                        ref={this.NSBoxString_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/classifier/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}